import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, fmtErr, API } from "../../lib/api";
import { industryByKey } from "../../lib/constants";
import IndustryBadge from "../../components/IndustryBadge";
import { Plus, EnvelopeSimple, Clock, Package, Warning, Download } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const STATUSES = ["draft", "sent", "acknowledged", "response_received", "under_review", "accepted", "rejected"];
const STATUS_COLORS = {
  draft: "var(--c-muted)", sent: "var(--c-accent)", acknowledged: "var(--c-amber)", response_received: "var(--c-green)",
  under_review: "var(--c-amber)", accepted: "var(--c-green)", rejected: "var(--c-red)",
};

const EMPTY = { project_id: "", component: "", category: "", specs_needed_text: "", quantity: "", drawings_requested: false, required_date: "", urgency: "normal", notes: "" };

export default function ScmInquiries() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filters, setFilters] = useState({ industry: "all", status: "all", vendor_id: "all", project_id: "all" });
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const orgIndustries = user?.org?.industries || [];

  const load = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "all"));
      const [s, inq, p, v] = await Promise.all([
        api.get("/scm/summary"), api.get("/scm/inquiries", { params }),
        api.get("/projects"), api.get("/vendors"),
      ]);
      setSummary(s.data); setInquiries(inq.data); setProjects(p.data); setVendors(v.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, [filters]);

  const exportXlsx = () => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "all"))).toString();
    window.open(`${API}/scm/inquiries/export.xlsx${qs ? `?${qs}` : ""}`, "_blank");
  };

  const create = async () => {
    if (!form.project_id || !form.component) { toast.error("Project and component required"); return; }
    try {
      await api.post("/scm/inquiries", {
        project_id: form.project_id, component: form.component, category: form.category,
        specs_needed: form.specs_needed_text.split("\n").map((s) => s.trim()).filter(Boolean),
        quantity: form.quantity, drawings_requested: form.drawings_requested,
        required_date: form.required_date, urgency: form.urgency, notes: form.notes,
      });
      toast.success("Inquiry created");
      setShowNew(false); setForm(EMPTY); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const Card = ({ title, value, icon: Icon, color, testid }) => (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{title}</span>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="text-3xl font-medium font-mono text-[var(--c-text)] mt-2">{value ?? "—"}</div>
    </div>
  );

  const FilterSel = ({ k, options, allLabel, testid }) => (
    <Select value={filters[k]} onValueChange={(v) => setFilters((f) => ({ ...f, [k]: v }))}>
      <SelectTrigger data-testid={testid} className="w-44 h-8 bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] text-xs"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
        <SelectItem value="all" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{allLabel}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-4" data-testid="scm-inquiries">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card title="Open Inquiries" value={summary?.open} icon={EnvelopeSimple} color="var(--c-accent)" testid="scm-summary-open" />
        <Card title="Awaiting Response" value={summary?.awaiting_response} icon={Clock} color="var(--c-amber)" testid="scm-summary-awaiting" />
        <Card title="Received" value={summary?.received} icon={Package} color="var(--c-green)" testid="scm-summary-received" />
        <Card title="Overdue" value={summary?.overdue} icon={Warning} color="var(--c-red)" testid="scm-summary-overdue" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <FilterSel k="industry" testid="scm-filter-industry" allLabel="All Industries"
          options={orgIndustries.map((k) => ({ value: k, label: industryByKey(k).name }))} />
        <FilterSel k="status" testid="scm-filter-status" allLabel="All Statuses"
          options={STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))} />
        <FilterSel k="vendor_id" testid="scm-filter-vendor" allLabel="All Vendors"
          options={vendors.map((v) => ({ value: v.id, label: v.name }))} />
        <FilterSel k="project_id" testid="scm-filter-project" allLabel="All Projects"
          options={projects.map((p) => ({ value: p.id, label: p.project_code }))} />
        <div className="flex-1" />
        <Button data-testid="export-inquiries-btn" variant="outline" onClick={exportXlsx}
          className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs mr-2">
          <Download size={14} className="mr-1" /> Export Excel
        </Button>
        <Button data-testid="new-inquiry-btn" onClick={() => setShowNew(true)}
          className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
          <Plus size={14} className="mr-1" /> New Inquiry
        </Button>
      </div>

      <div className="border border-[var(--c-border)] rounded-sm bg-[var(--c-surface)] overflow-x-auto">
        <table className="w-full text-sm" data-testid="scm-table">
          <thead>
            <tr className="border-b border-[var(--c-border)]">
              {["Inquiry ID", "Project", "Industry", "Component", "Vendor", "Status", "Expected", "Days Left"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {inquiries.map((q) => (
              <tr key={q.id} data-testid={`scm-row-${q.inquiry_id}`} onClick={() => nav(`/scm/inquiries/${q.id}`)}
                className="border-b border-[var(--c-border)]/60 last:border-0 hover:bg-[var(--c-border)]/25 cursor-pointer transition-colors duration-150">
                <td className="px-4 py-3 font-mono text-xs text-[var(--c-accent)] whitespace-nowrap">{q.inquiry_id}</td>
                <td className="px-4 py-3 font-mono text-xs text-[var(--c-muted)]">{q.project_code}</td>
                <td className="px-4 py-3"><IndustryBadge industryKey={q.industry_id} /></td>
                <td className="px-4 py-3 text-xs text-[var(--c-text)]">{q.component}</td>
                <td className="px-4 py-3 text-xs text-[var(--c-muted)]">{vendors.find((v) => v.id === q.vendor_id)?.name || "—"}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border whitespace-nowrap"
                    style={{ color: STATUS_COLORS[q.status], borderColor: `color-mix(in srgb, ${STATUS_COLORS[q.status]} 25%, transparent)`, backgroundColor: `color-mix(in srgb, ${STATUS_COLORS[q.status]} 10%, transparent)` }}
                    data-testid={`scm-status-${q.inquiry_id}`}>
                    {q.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-[var(--c-muted)]">{q.expected_date || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs" data-testid={`scm-days-${q.inquiry_id}`}>
                  {q.days_remaining == null ? <span className="text-[var(--c-muted)]">—</span>
                    : <span className={q.overdue ? "text-[var(--c-red)]" : "text-[var(--c-text)]"}>{q.days_remaining}d{q.overdue && " OVERDUE"}</span>}
                </td>
              </tr>
            ))}
            {inquiries.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-[var(--c-muted)]" data-testid="scm-empty">
                No inquiries. They're auto-created on project finalization (SCM auto-check) or add one manually.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-w-xl" data-testid="new-inquiry-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)]">New SCM Inquiry</DialogTitle>
            <DialogDescription className="sr-only">Create a vendor inquiry for a bought-out component</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Project *</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="ni-project" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)] max-h-56">
                  {projects.map((p) => <SelectItem key={p.id} value={p.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{p.project_code} · {p.product_summary}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Component *</Label>
                <Input data-testid="ni-component" value={form.component} onChange={(e) => setForm({ ...form, component: e.target.value })}
                  placeholder="e.g., OLTC 33kV 200A" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Category (BOM group)</Label>
                <Input data-testid="ni-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Tap Changer" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Specs needed from vendor (one per line)</Label>
              <textarea data-testid="ni-specs" value={form.specs_needed_text} onChange={(e) => setForm({ ...form, specs_needed_text: e.target.value })}
                rows={3} placeholder={"Overall dimensions\nTap positions & step voltage\nRated current"}
                className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-3 py-2 text-xs text-[var(--c-text)] focus:outline-none focus:ring-2 focus:ring-[var(--c-accent)]" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Quantity</Label>
                <Input data-testid="ni-quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Required response by</Label>
                <Input data-testid="ni-required-date" type="date" value={form.required_date} onChange={(e) => setForm({ ...form, required_date: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Urgency</Label>
                <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                  <SelectTrigger data-testid="ni-urgency" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    {["high", "normal", "low"].map((u) => <SelectItem key={u} value={u} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="ni-drawings" checked={form.drawings_requested} onCheckedChange={(v) => setForm({ ...form, drawings_requested: v })} />
              <Label className="text-[var(--c-muted)] text-xs">Request drawings/datasheets with offer</Label>
            </div>
            <Button data-testid="ni-submit" onClick={create} className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">Create Inquiry</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
